// The global avatar element catalogue — the avatars.json blob served by the
// backend's getAvatars endpoint and written by setAvatars. NOT part of a
// branch's RemoteConstants: avatars are global, unthemed, and saved
// immediately (no export step, no undo history).
//
// Element ids are stable integers, allocated per type from `nextIds` and NEVER
// reused: deleting an element leaves a permanent hole, and `nextIds` only ever
// grows. Player avatars reference elements by (type, id), so a reused id would
// silently swap art under existing avatars.

// Draw order, back to front: face, eyes, facialHair, hair, hat.
export const AVATAR_ELEMENT_TYPES = ['face', 'eyes', 'facialHair', 'hair', 'hat'] as const;
export type AvatarElementType = typeof AVATAR_ELEMENT_TYPES[number];

// Slots a player avatar may omit entirely.
export const OPTIONAL_AVATAR_TYPES: readonly AvatarElementType[] = ['facialHair', 'hat'];

export const AVATAR_TYPE_LABELS: Record<AvatarElementType, string> = {
    face: 'Face',
    eyes: 'Eyes',
    facialHair: 'Facial Hair',
    hair: 'Hair',
    hat: 'Hat',
};

export interface AvatarElement {
    id: number;
    fileName: string;
    srcWidth?: number;
    srcHeight?: number;
}

export interface AvatarCatalogue {
    version: number;
    elements: Record<AvatarElementType, AvatarElement[]>;
    nextIds: Record<AvatarElementType, number>;
    // Per-type color palettes: "RRGGBB" hex (no '#', no alpha), matching the
    // color format stored in player avatar specs. The palette constrains what
    // the client's randomizer (and later, a manual picker) offers; an EMPTY
    // palette means "unconstrained" — the client falls back to random RGB.
    // Specs store the resolved color, so palette edits never recolor existing
    // player avatars.
    palettes: Record<AvatarElementType, string[]>;
}

// The backend's no-blob fallback shape (version 0, ids start at 1). For
// display only — never save this over a catalogue that failed to load.
export function emptyAvatarCatalogue(): AvatarCatalogue {
    return {
        version: 0,
        elements: {face: [], eyes: [], facialHair: [], hair: [], hat: []},
        nextIds: {face: 1, eyes: 1, facialHair: 1, hair: 1, hat: 1},
        palettes: {face: [], eyes: [], facialHair: [], hair: [], hat: []},
    };
}

// Catalogues saved before palettes existed lack the key — fill it in so the
// rest of the editor can assume the full shape.
export function normalizeCatalogue(catalogue: AvatarCatalogue): AvatarCatalogue {
    if (catalogue.palettes) return catalogue;
    return {...catalogue, palettes: emptyAvatarCatalogue().palettes};
}
